import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';

import ActionBar from '../components/ActionBar';
import AlertBar from '../components/AlertBar';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import AbstractBackground from '../components/AbstractBackground';
import Footer from '../components/Footer';

import { clearMessages } from '../reducers/messagesReducer';
import {
  clearSelectedTileId,
  setIsActiveChat,
  setShowTiles,
  clearPromptItems,
  hideToast,
} from '../reducers/uiReducer';

import useResponsive from '../hooks/useResponsive';
import actionBarConfig from '../config/actionBarConfig';

const Admin = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const loading = useSelector((state) => state.loading);
  const toast = useSelector((state) => state.ui.toast);
  const containerRef = useRef(null);
  const { isTabletScreen, isMobileScreen } = useResponsive();

  const handleNewChat = () => {
    dispatch(clearMessages());
    dispatch(clearSelectedTileId());
    dispatch(setShowTiles(true));
    dispatch(setIsActiveChat(false));
    dispatch(clearPromptItems());
  };

  const memoizedBackground = useMemo(
    () => <AbstractBackground animate={loading} containerRef={containerRef} />,
    [loading]
  );

  const breadcrumbPath = [{ label: 'Admin Dashboard' }];

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box
        ref={containerRef}
        sx={{
          height: '100vh',
          width: '100%',
          minWidth: 300,
          maxWidth: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {memoizedBackground}

        {/* Action Bar */}
        <Box
          sx={{
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.grey[900]
                : theme.palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ActionBar config={actionBarConfig} onNewChat={handleNewChat} />
        </Box>

        {/* Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            display: 'flex',
            position: 'relative',
            overflowY: 'auto',
            boxShadow: 1,
            background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(to top,rgba(18, 18, 18, 0.8),rgba(29, 29, 29, 0.6))'
                            : 'linear-gradient(to top, rgba(255,255,255,0.2), rgba(0,116,211,0.1))',
          }}
        >
          <AlertBar
            open={toast.open}
            onClose={() => dispatch(hideToast())}
            severity={toast.severity}
            message={toast.message}
          />

          <Box
            sx={{
              display: 'flex',
              width: '100%',
              overflow: 'hidden',
              flexDirection: 'column',
              backgroundColor: theme.palette.background.paper,
              padding: 2,
              margin: 2,
              borderRadius: 2,
              boxShadow: 1,
              alignSelf: 'flex-start',
            }}
          >
            <AdminBreadcrumb path={breadcrumbPath} />

            <Box sx={{ flex: 1, padding: 2 }}>
              {[
                { label: 'Users Manager', to: '/admin/users' },
                { label: 'Invite User', to: '/admin/invite' },
                { label: 'Achievements', to: '/admin/achievements' },
                { label: 'FAQ', to: '/admin/faq' },
              ].map(({ label, to }, index) => (
                <Link
                  key={index}
                  to={to}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        color: theme.palette.primary.main,
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {label}
                  </Typography>
                </Link>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Footer
          sx={{
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );
};

export default Admin;
